import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Table, Modal, Badge } from 'react-bootstrap';
import './MessagesContent.css';
import apiClient, { getUserInfo } from '../../services/apiClient';

// ─────────────────────────────────────────────
// Допоміжні константи
// ─────────────────────────────────────────────

const MESSAGE_TYPES = ['', 'Повідомлення', 'Скарга', 'Потрібна допомога'];

const MESSAGE_TYPE_KEYS = {
  '': 'none',
  'Повідомлення': 'info',
  'Скарга': 'claim',
  'Потрібна допомога': 'needhelp',
};

const MESSAGE_TYPE_DAYS = { none: 0, info: 5, claim: 7, needhelp: 3 };

const CHILD_STATUS_KEYS = {
  'Заявка моєї дитини на вступ': 'myrequest',
  'Моя дитина в садочку': 'mychildren',
  'Заявка іншої дитини': 'otherrequest',
  'Інша дитина в садочку': 'otherchildren',
};

const CHILD_STATUSES = Object.keys(CHILD_STATUS_KEYS);

const TYPE_BADGE_VARIANT = {
  'Повідомлення': 'primary',
  'Скарга': 'danger',
  'Потрібна допомога': 'warning',
  '': 'secondary',
};

// ─────────────────────────────────────────────
// Функція формування тіла запиту
// ─────────────────────────────────────────────

function buildMessageBody(step1, step2) {
  const typeKey = MESSAGE_TYPE_KEYS[step1.needResponse] || 'none';
  const days = MESSAGE_TYPE_DAYS[typeKey];
  const about = step2.about || step1.message.slice(0, 25) + ' ...';

  return {
    message_data: {
      genInfo: {
        author: {
          name: step2.authorName,
          email: step2.authorEmail,
          phone: step2.authorPhone,
        },
        about,
        child: {
          birthCertificateNumber: step2.birthCert,
          firstName: step2.childName,
          status: CHILD_STATUS_KEYS[step2.childStatus] || '',
        },
        needResponse: {
          status: typeKey,
          dateTo: new Date(Date.now() + 24 * 60 * 60 * 1000 * days),
        },
        result: {
          dateFinish: new Date(),
          statusResult: 'new',
        },
      },
      messages: [
        {
          date: new Date(),
          text: step1.message,
          attach: { body: '', name: '' },
          from: { name: step2.authorName, email: step2.authorEmail },
        },
      ],
    },
  };
}

// ─────────────────────────────────────────────
// Компонент: Деталі звернення (діалог-листування)
// ─────────────────────────────────────────────

function DetailModal({ show, onHide, data, userEmail }) {
  if (!data || !data.length) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Історія повідомлень</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mc-detail-body">
        {data.map((msg, idx) => {
          const isOwn = msg.from?.email === userEmail;
          return (
            <div
              key={idx}
              className={`mc-chat-row ${isOwn ? 'own' : 'support'}`}
            >
              <div className="mc-chat-bubble">
                <div className="mc-chat-meta">
                  <span className="mc-chat-author">
                    {isOwn ? 'Ви' : 'Техпідтримка'}
                  </span>
                  <span className="mc-chat-date">
                    {new Date(msg.date).toLocaleDateString('uk-UA', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="mc-chat-text">{msg.text}</div>
              </div>
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Закрити</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─────────────────────────────────────────────
// Компонент: Крок 1 — Текст звернення
// ─────────────────────────────────────────────

function Step1({ data, onChange, errors }) {
  return (
    <div className="mc-wizard-step">
      <p className="mc-wizard-hint">
        Спочатку додайте текст звернення. На наступному кроці Ви зможете додати інформацію про дитину.
      </p>

      <Form.Group className="mb-3">
        <Form.Label>Тип звернення <span className="text-danger">*</span></Form.Label>
        <Form.Select
          value={data.needResponse}
          onChange={e => onChange('needResponse', e.target.value)}
          isInvalid={!!errors.needResponse}
        >
          {MESSAGE_TYPES.map(t => (
            <option key={t} value={t}>{t || '— оберіть тип —'}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{errors.needResponse}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Текст повідомлення <span className="text-danger">*</span></Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          placeholder="Стисло опишіть суть питання"
          value={data.message}
          onChange={e => onChange('message', e.target.value)}
          isInvalid={!!errors.message}
        />
        <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
      </Form.Group>
    </div>
  );
}

// ─────────────────────────────────────────────
// Компонент: Крок 2 — Додаткова інформація
// ─────────────────────────────────────────────

function Step2({ data, onChange, errors }) {
  return (
    <div className="mc-wizard-step">
      <p className="mc-wizard-hint">
        Заповніть, будь ласка, інформацію, щоб ми мали змогу ефективно Вам допомогти.
      </p>

      <fieldset className="mc-wizard-fieldset mb-4">
        <legend>Тема звернення</legend>
        <Form.Group>
          <Form.Label>Короткий опис <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Додайте короткий опис (не менше 25 символів)"
            value={data.about}
            onChange={e => onChange('about', e.target.value)}
            isInvalid={!!errors.about}
          />
          <Form.Control.Feedback type="invalid">{errors.about}</Form.Control.Feedback>
        </Form.Group>
      </fieldset>

      <fieldset className="mc-wizard-fieldset mb-4">
        <legend>Про дитину</legend>

        <Form.Group className="mb-3">
          <Form.Label>Статус</Form.Label>
          <Form.Select
            value={data.childStatus}
            onChange={e => onChange('childStatus', e.target.value)}
          >
            <option value="">— оберіть —</option>
            {CHILD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Form.Select>
        </Form.Group>

        <div className="mc-wizard-grid-2">
          <Form.Group className="mb-3">
            <Form.Label>Ім'я дитини <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Ім'я"
              value={data.childName}
              onChange={e => onChange('childName', e.target.value)}
              isInvalid={!!errors.childName}
            />
            <Form.Control.Feedback type="invalid">{errors.childName}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Свідоцтво про народження <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Номер свідоцтва"
              value={data.birthCert}
              onChange={e => onChange('birthCert', e.target.value)}
              isInvalid={!!errors.birthCert}
            />
            <Form.Control.Feedback type="invalid">{errors.birthCert}</Form.Control.Feedback>
          </Form.Group>
        </div>
      </fieldset>

      <fieldset className="mc-wizard-fieldset">
        <legend>Мої контакти</legend>
        <div className="mc-wizard-grid-3">
          <Form.Group className="mb-3">
            <Form.Label>Як до Вас звертатись? <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Ваше ім'я"
              value={data.authorName}
              onChange={e => onChange('authorName', e.target.value)}
              isInvalid={!!errors.authorName}
            />
            <Form.Control.Feedback type="invalid">{errors.authorName}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Пошта <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              placeholder="email@example.com"
              value={data.authorEmail}
              onChange={e => onChange('authorEmail', e.target.value)}
              isInvalid={!!errors.authorEmail}
            />
            <Form.Control.Feedback type="invalid">{errors.authorEmail}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Телефон <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="tel"
              placeholder="+380XXXXXXXXX"
              value={data.authorPhone}
              onChange={e => onChange('authorPhone', e.target.value)}
              isInvalid={!!errors.authorPhone}
            />
            <Form.Control.Feedback type="invalid">{errors.authorPhone}</Form.Control.Feedback>
          </Form.Group>
        </div>
      </fieldset>
    </div>
  );
}

// ─────────────────────────────────────────────
// Компонент: Wizard — Нове звернення
// ─────────────────────────────────────────────

const EMPTY_STEP1 = { needResponse: '', message: '' };

function buildStep2Initial(userInfo) {
  const user = userInfo?.authObj?.user;
  return {
    about: '',
    childStatus: '',
    childName: '',
    birthCert: '',
    authorName: user?.name?.firstName || '',
    authorEmail: user?.email || '',
    authorPhone: user?.phone || '',
  };
}

function validateStep1(data) {
  const errors = {};
  if (!data.needResponse) errors.needResponse = 'Оберіть тип звернення';
  if (!data.message || data.message.trim().length < 5)
    errors.message = 'Введіть текст звернення (мін. 5 символів)';
  return errors;
}

function validateStep2(data) {
  const errors = {};
  if (!data.about || data.about.trim().length < 25)
    errors.about = 'Мінімум 25 символів';
  if (!data.childName || data.childName.trim().length < 2)
    errors.childName = "Введіть ім'я дитини";
  if (!data.birthCert || data.birthCert.trim().length < 1)
    errors.birthCert = 'Введіть номер свідоцтва';
  if (!data.authorName) errors.authorName = "Введіть ім'я";
  if (!data.authorEmail || !/\S+@\S+\.\S+/.test(data.authorEmail))
    errors.authorEmail = 'Невірний формат пошти';
  if (!data.authorPhone || data.authorPhone.replace(/\D/g, '').length < 10)
    errors.authorPhone = 'Мінімум 10 цифр';
  return errors;
}

function CreateMessageModal({ show, onHide, userInfo, onSend }) {
  const [step, setStep] = useState(1);
  const [step1, setStep1] = useState(EMPTY_STEP1);
  const [step2, setStep2] = useState(() => buildStep2Initial(userInfo));
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  // Скидання при відкритті
  useEffect(() => {
    if (show) {
      setStep(1);
      setStep1(EMPTY_STEP1);
      setStep2(buildStep2Initial(userInfo));
      setErrors({});
      setSending(false);
    }
  }, [show]);

  const changeStep1 = useCallback((key, val) => {
    setStep1(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }, []);

  const changeStep2 = useCallback((key, val) => {
    setStep2(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }, []);

  const handleNext = () => {
    const errs = validateStep1(step1);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    // Якщо about ще пустий — заповнити автоматично
    if (!step2.about) {
      setStep2(prev => ({ ...prev, about: step1.message.slice(0, 25) + ' ...' }));
    }
    setErrors({});
    setStep(2);
  };

  const handleBack = () => { setErrors({}); setStep(1); };

  const handleSubmit = async () => {
    const errs = validateStep2(step2);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSending(true);
    try {
      await onSend(buildMessageBody(step1, step2));
      onHide();
    } catch (e) {
      console.error(e);
      alert('Помилка при надсиланні. Спробуйте ще раз.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 1 ? 'Нове звернення — Крок 1 з 2' : 'Нове звернення — Крок 2 з 2'}
        </Modal.Title>
      </Modal.Header>

      {/* Індикатор прогресу */}
      <div className="mc-wizard-progress">
        <div className={`mc-wizard-progress-step ${step >= 1 ? 'active' : ''}`}>
          <span>1</span> Текст звернення
        </div>
        <div className="mc-wizard-progress-line" />
        <div className={`mc-wizard-progress-step ${step >= 2 ? 'active' : ''}`}>
          <span>2</span> Додаткова інформація
        </div>
      </div>

      <Modal.Body>
        <div className="mc-modal-body-fix">
          {step === 1
            ? <Step1 data={step1} onChange={changeStep1} errors={errors} />
            : <Step2 data={step2} onChange={changeStep2} errors={errors} />
          }
        </div>
      </Modal.Body>

      <Modal.Footer>
        {step === 1 ? (
          <>
            <Button variant="secondary" onClick={onHide}>Скасувати</Button>
            <Button variant="primary" onClick={handleNext}>
              Далі <i className="fa-solid fa-arrow-right ms-1" />
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={handleBack}>
              <i className="fa-solid fa-arrow-left me-1" /> Назад
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={sending}>
              {sending
                ? <><span className="spinner-border spinner-border-sm me-2" />Надсилання...</>
                : <><i className="fa-solid fa-paper-plane me-2" />Направити</>
              }
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ─────────────────────────────────────────────
// Компонент: Головний — таблиця зі зверненнями
// ─────────────────────────────────────────────

const MessagesContent = ({ onInfo }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  // Модальні
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const userInfo = getUserInfo();


  const userEmail = userInfo?.user?.email || '';

  // ── Завантаження ──
  useEffect(() => {

    const api = async () => {
      const email = userEmail || 'all';
      const dateFrom = '2019-12-01';
      const dateTo = new Date().toISOString();

      if (email !== 'all' && !/[a-z\d]{1,30}@[a-z\d.]{1,30}/.test(email)) {
        alert('Невірний формат пошти');
        setLoading(false);
        return;
      }

      const link = `messages_list?email=${email}&dateFrom=${dateFrom}&dateTo=${dateTo}&status=all`;

      const data = await apiClient.get(link);

      setMessages(data.data);
      setLoading(false);

    }

    api();


  }, []);


  useEffect(()=>{

    const numberAll = messages.length;
    const numberNew = messages.filter(m=>["new"].includes(m.genInfo.result.statusResult));

    if(onInfo) onInfo({all: numberAll, new: numberNew});

  }, [messages])

  // ── Фільтрація ──
  const filtered = messages.filter(msg => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      msg.genInfo?.about?.toLowerCase().includes(q) ||
      msg.genInfo?.child?.firstName?.toLowerCase().includes(q) ||
      msg.messages?.[0]?.text?.toLowerCase().includes(q)
    );
  });

  // ── Вибір рядків ──
  const allSelected = filtered.length > 0 && selectedIds.length === filtered.length;

  const toggleSelectAll = e => {
    setSelectedIds(e.target.checked ? filtered.map((_, i) => i) : []);
  };

  const toggleSelect = (idx, checked) => {
    setSelectedIds(prev =>
      checked ? [...prev, idx] : prev.filter(i => i !== idx)
    );
  };

  // ── Надсилання звернення ──
  const handleSend = async (body) => {

    const result = await apiClient.post('messages', body);
    alert('Ваше звернення успішно відправлено. В найближчий час Вам нададуть відповідь.');

    setMessages(prev => {
        // Якщо сервер повернув нову запис — можна додати; інакше просто залишаємо
        if (result?.data[0]) return [result.data[0], ...prev];
        return prev;
      });

  };




  // ── Рядок таблиці ──
  const getStatusText = msg => {
    if (msg.messages?.length > 1) return 'відповіли';
    return 'очікується';
  };

  const getTypeLabel = msg => {
    const statusKey = msg.genInfo?.needResponse?.status;
    const entry = Object.entries(MESSAGE_TYPE_KEYS).find(([, v]) => v === statusKey);
    return entry ? entry[0] : '';
  };

  return (
    <div className="messages-content">
      <div className="mc-content-wrapper">
        <h1 className="mc-page-title">Мої повідомлення</h1>

        <div className="mc-toolbar">
          <div className="mc-left-tools">
            <Button className="mc-btn-new" onClick={() => setCreateOpen(true)}>
              <i className="fa-solid fa-plus me-2" />Нове повідомлення
            </Button>
          </div>
          <div className="mc-right-tools">
            <div className="mc-search-box">
              <Form.Control
                type="text"
                className="mc-search-input"
                placeholder="Швидкий пошук"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <i className="fa-solid fa-magnifying-glass" />
            </div>
          </div>
        </div>

        <div className="mc-table-wrapper">
          {loading ? (
            <div className="text-center py-5 text-muted">
              <span className="spinner-border me-2" />Завантаження...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {search ? 'Нічого не знайдено' : 'Звернень поки немає'}
            </div>
          ) : (
            <Table className="mc-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <Form.Check
                      type="checkbox"
                      aria-label="Вибрати все"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Дата звернення</th>
                  <th>Питання</th>
                  <th>Тип</th>
                  <th>Про кого</th>
                  <th>Статус відповіді</th>
                  <th>Відповідь</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((message, idx) => {
                  const typeLabel = getTypeLabel(message);
                  const hasAnswer = message.messages?.length > 1;
                  return (
                    <tr
                      key={message._id || idx}
                      className="mc-tr-hover"
                      onClick={() => {
                        setDetailData(message.messages || []);
                        setDetailOpen(true);
                      }}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          aria-label="Вибрати"
                          checked={selectedIds.includes(idx)}
                          onChange={e => toggleSelect(idx, e.target.checked)}
                        />
                      </td>
                      <td data-label="Дата звернення">
                        {new Date(message.createdAt).toLocaleDateString('uk-UA')}
                      </td>
                      <td data-label="Питання">
                        <span title={message.messages?.[0]?.text}>
                          {message.genInfo?.about}
                        </span>
                      </td>
                      <td data-label="Тип">
                        {typeLabel && (
                          <Badge bg={TYPE_BADGE_VARIANT[typeLabel] || 'secondary'}>
                            {typeLabel}
                          </Badge>
                        )}
                      </td>
                      <td data-label="Про кого">
                        {message.genInfo?.child?.firstName}
                      </td>
                      <td
                        data-label="Статус"
                        className={hasAnswer ? 'mc-answer-date' : 'text-muted'}
                      >
                        {hasAnswer
                          ? new Date(message.updatedAt).toLocaleDateString('uk-UA')
                          : 'очікується'}
                      </td>
                      <td data-label="Відповідь" className="mc-answer-text">
                        {hasAnswer ? (
                          <span title={message.messages.slice(1).map(el => el.text).join('; ')}>
                            {message.messages.length - 1} повідомл.
                          </span>
                        ) : (
                          <span className="text-muted">очікується</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      {/* Модальне вікно деталей */}
      <DetailModal
        show={detailOpen}
        onHide={() => setDetailOpen(false)}
        data={detailData}
        userEmail={userEmail}
      />

      {/* Модальне вікно створення */}
      <CreateMessageModal
        show={createOpen}
        onHide={() => setCreateOpen(false)}
        userInfo={userInfo}
        onSend={handleSend}
      />
    </div>
  );
};

export default MessagesContent;